import { ArrowDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '../types/chat';
import { MessageItem } from './MessageItem';

type ConversationViewProps = {
  messages: ChatMessage[];
  followSignal: number;
  onCopy: (message: ChatMessage) => void;
  onEdit: (message: ChatMessage) => void;
  onRegenerate: (message: ChatMessage) => void;
};

export function ConversationView(props: ConversationViewProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const followRef = useRef(true);
  const [showLatest, setShowLatest] = useState(false);
  const streamFingerprint = props.messages.map((message) => {
    const contentLength = typeof message.content === 'string' ? message.content.length : message.content.length;
    return `${message.id}:${contentLength}:${message.reasoning?.length ?? 0}:${message.status}`;
  }).join('|');

  const scrollToLatest = (behavior: ScrollBehavior = 'smooth') => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    followRef.current = true;
    setShowLatest(false);
    viewport.scrollTo({ top: viewport.scrollHeight, behavior });
  };

  useEffect(() => {
    if (followRef.current) scrollToLatest('auto');
  }, [streamFingerprint]);

  useEffect(() => {
    scrollToLatest('auto');
  }, [props.followSignal]);

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={viewportRef}
        className="absolute inset-0 overflow-y-auto overscroll-contain px-3 md:px-5 lg:px-8"
        onScroll={(event) => {
          const target = event.currentTarget;
          const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 96;
          followRef.current = nearBottom;
          setShowLatest(!nearBottom);
        }}
      >
        <div className="mx-auto flex w-full max-w-[760px] flex-col gap-8 py-6 sm:py-8">
          {props.messages.map((message) => (
            <MessageItem
              key={message.id}
              message={message}
              onCopy={props.onCopy}
              onEdit={props.onEdit}
              onRegenerate={props.onRegenerate}
            />
          ))}
        </div>
      </div>
      {showLatest && (
        <button type="button" onClick={() => scrollToLatest()} className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full border border-border bg-surface-raised px-3 py-2 text-xs text-primary shadow-popover">
          <ArrowDown className="size-3.5" />Ke pesan terbaru<span className="size-1.5 rounded-full bg-accent" />
        </button>
      )}
    </div>
  );
}
